import { cn, timeAgo } from '@util/index';
import { useNotification } from 'context/NotificationContext';
import type { NotificationDocument } from '@models/notification';
import Link from 'next/link';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import SafeImage from '@ui/SafeImage';
import { getProductsByIds } from '@util/firestore/products';
import { getUserByUsername } from '@util/firestore/users';

function NotificationItem({
  notification,
  preventDefault,
  hasUnreadMessages,
  noWrapper,
}: {
  notification: NotificationDocument;
  preventDefault?: boolean;
  hasUnreadMessages?: boolean;
  noWrapper?: boolean;
}) {
  const { setShow, markAsReadMutation } = useNotification();
  const queryClient = useQueryClient();

  const { data: products } = useQuery({
    queryKey: ['products', notification.product_ids ?? []],
    queryFn: () => getProductsByIds(notification.product_ids ?? []),
    enabled: !!notification.product_ids?.length,
  });

  const getBadgeConfig: () =>
    | { text: string; backgroundColor?: string; foregroundColor?: string }
    | undefined = () => {
    const messageLower = notification.message.toLowerCase();
    const titleLower = notification.title?.toLowerCase() ?? '';

    const textIncludes = (text: string) => {
      return messageLower.includes(text) || titleLower.includes(text);
    };

    if (textIncludes('% off') || textIncludes('$ off')) {
      return {
        text: 'Price Drop',
      };
    }

    if (notification.key === 'offer') {
      return {
        text: 'offer',
      };
    }

    if (textIncludes('new order')) {
      return {
        text: 'New Order',
      };
    }

    if (
      notification.key === 'order_seller' &&
      textIncludes('awaiting shipment')
    ) {
      return {
        text: 'Ship Now',
      };
    }

    if (textIncludes('new bid')) {
      return {
        text: 'New Bid',
        backgroundColor: '#7E7E7E',
        foregroundColor: '#000000',
      };
    }

    if (textIncludes('order has been delivered')) {
      return {
        text: 'Order Delivered',
        backgroundColor: '#7E7E7E',
        foregroundColor: '#FFFFFF',
      };
    }

    if (notification.title.toLowerCase().includes('message')) {
      return {
        text: 'New Message',
        backgroundColor: '#7E7E7E',
        foregroundColor: '#FFFFFF',
      };
    }

    if (notification.title.toLowerCase().includes('been paid')) {
      return {
        text: 'Funds available',
        backgroundColor: '#13BB56',
        foregroundColor: '#FFFFFF',
      };
    }

    if (notification.title.toLowerCase().includes('new review')) {
      return {
        text: 'New Review',
        backgroundColor: '#7E7E7E',
        foregroundColor: '#FFFFFF',
      };
    }
  };

  const getNotificationImages = () => {
    if (
      !!notification.product_ids?.length &&
      notification.product_ids.length > 1
    ) {
      return products?.map((product) => product.thumbnail);
    }
  };

  const notificationImages = getNotificationImages();
  const badge = getBadgeConfig();

  const isMessageNotification =
    notification.title?.includes('New message from');

  const senderUsername = isMessageNotification
    ? notification.title?.replace('New message from ', '').trim()
    : undefined;

  const { data: user } = useQuery({
    queryKey: ['user', senderUsername],
    queryFn: () => {
      if (!senderUsername) return null;
      return getUserByUsername(senderUsername);
    },
    enabled: !!isMessageNotification && !!senderUsername,
  });

  const getImageSource = () => {
    if (isMessageNotification && user?.photo) {
      return user.photo;
    }

    if (notificationImages?.length === 1 || !notificationImages?.length) {
      return notification.thumbnail;
    }

    return null;
  };

  const imageSrc = getImageSource();

  const contentInner = (
    <>
      <div
        className={cn(
          hasUnreadMessages && !!notification.read && 'pl-7',
          'flex items-center gap-4'
        )}
      >
        {!notification.read && (
          <div className="h-4 w-4 rounded-full bg-brand-secondary" />
        )}

        {notificationImages && notificationImages.length > 1 ? (
          <div className="relative h-[10.5rem] w-[10.5rem]">
            <SafeImage
              src={notificationImages[0]}
              alt="notification image"
              className="absolute bottom-0 z-0 h-full w-full shrink-0 -rotate-12 rounded-[2rem] bg-zinc-50 object-cover shadow-sm transition-all duration-75 ease-linear group-hover:rotate-0"
            />

            <SafeImage
              src={notificationImages[1]}
              alt="notification image"
              className="relative z-10 h-full w-full shrink-0 rounded-[2rem] border-2 border-brand-primary-lighter bg-zinc-50 object-cover shadow-md"
            />
          </div>
        ) : (
          <SafeImage
            src={imageSrc}
            alt="notification image"
            className="h-[10.5rem] w-[10.5rem] shrink-0 rounded-[2rem] border border-brand-primary-lighter bg-zinc-50 object-cover shadow-sm"
            fallback={
              <div className="flex h-[10.5rem] w-[10.5rem] shrink-0 items-center justify-center rounded-[2rem] border border-brand-primary-lighter bg-zinc-50 object-cover shadow-sm">
                <span className="text-[1.4rem] font-medium text-gray-500">
                  No image
                </span>
              </div>
            }
          />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2">
        <div className="flex w-full items-start justify-between gap-4">
          <div className="flex flex-1 flex-col gap-2">
            {badge && (
              <div
                className={cn(
                  'w-fit rounded-[1rem] px-4 py-2 text-center text-[1.4rem] font-semibold uppercase text-white'
                )}
                style={{
                  backgroundColor: badge.backgroundColor ?? '#C00000',
                  color: badge.foregroundColor ?? '#FFFFFF',
                }}
              >
                {badge.text}
              </div>
            )}

            <span className="line-clamp-2 text-[1.8rem] font-semibold">
              {notification.title}
            </span>
          </div>

          <span className="shrink-0 whitespace-nowrap text-[1.4rem] font-medium text-gray-500">
            {timeAgo(notification.created)}
          </span>
        </div>

        <span className="line-clamp-1 text-[1.5rem] text-zinc-700 sm:line-clamp-2">
          {notification.message}
        </span>
      </div>
    </>
  );

  if (noWrapper) {
    return <div className="flex w-full items-start gap-6">{contentInner}</div>;
  }

  return (
    <Link
      href={notification.web_url}
      onClick={(e) => {
        if (preventDefault) {
          e.preventDefault();
          return;
        }
        // mark notification as read
        markAsReadMutation([notification.id]);
        queryClient.invalidateQueries({
          queryKey: ['notifications'],
        });
        setShow(false);
      }}
      className="group flex w-full items-start gap-6 p-6 transition-colors duration-75 hover:bg-zinc-100"
    >
      {contentInner}
    </Link>
  );
}

export { NotificationItem };
